import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import articleService from '../services/articleService';
import MarkdownRenderer from './MarkdownRenderer';
import MediaUploadModal from './MediaUploadModal';
import QuoteModal from './QuoteModal';
import SliderModal from './SliderModal';
import ErrorModal from './ErrorModal';
import { API_URL } from '../config';
import {
  CloseIcon, BoldIcon, ItalicIcon, LinkIcon, ListIcon, QuoteIcon,
  CodeIcon, ImageIcon, SpinnerIcon, AlignJustifyIcon, SlidersIcon
} from './Icons';
import '../styles/DynamicBlocksEditor.css';

const CreateArticleModal = ({ isOpen, onClose, onSubmit, initialData, isEditing = false, documentId }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [content, setContent] = useState('');
  const [slug_field, setSlugField] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categories, setCategories] = useState([]);
  const [coverImage, setCoverImage] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [isPreview, setIsPreview] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const [isMediaModalOpen, setIsMediaModalOpen] = useState(false);
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
  const [isSliderModalOpen, setIsSliderModalOpen] = useState(false);
  const [editingBlockId, setEditingBlockId] = useState(null);
  const [errorModal, setErrorModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'error'
  });

  
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const draftSaveTimerRef = useRef(null);

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Load categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await articleService.getCategories();
        // Handle both direct array and nested data structure
        const categoriesData = response.data || response || [];
        setCategories(Array.isArray(categoriesData) ? categoriesData : []);
      } catch (error) {
        console.error('获取分类失败:', error);
      }
    };
    
    fetchCategories();
  }, []);

  // Validate category by ID (暂时禁用，因为分类已从前端获取)
  const validateCategory = async (categoryId) => {
    return true; // 暂时跳过分类验证
  };

  // Slug field is now manually entered by user (removed auto-generation)
  // Since user primarily uses Chinese content, auto-generation from title is not suitable

  // 反向解析函数：将blocks数组重新插入到content中
  const parseBlocksToContent = (content, blocks) => {
    if (!content || !blocks || blocks.length === 0) {
      return content || '';
    }

    // 创建组件占位符的映射
    const componentPlaceholders = {
      'shared.media': (block, componentId) => {
        const title = block.title || '媒体组件';
        const description = block.description || '点击编辑媒体内容';
        return `\n\n<!-- COMPONENT:MEDIA:${componentId} -->\n![${title}](# "${description}")\n<!-- /COMPONENT -->\n\n`;
      },
      'shared.quote': (block, componentId) => {
        const content = block.content || '点击编辑引用内容';
        const author = block.author || '';
        const about = block.about || '';

        let authorInfo = '';
        if (author || about) {
          authorInfo = `<cite>— <span class="author">${author}</span>${author && about ? ', ' : ''}${about}</cite>`;
        }

        return `\n\n<!-- COMPONENT:QUOTE:${componentId} -->\n<blockquote class="shared-quote">\n<p>${content}</p>\n${authorInfo}\n</blockquote>\n<!-- /COMPONENT -->\n\n`;
      },
      'shared.slider': (block, componentId) => {
        const fileCount = block.files?.length || 0;
        return `\n\n<!-- COMPONENT:SLIDER:${componentId} -->\n[轮播图片组件 - ${fileCount}张图片](#)\n<!-- /COMPONENT -->\n\n`;
      },
      'shared.rich-text': (block, componentId) => {
        const body = block.body || '富文本内容';
        return `\n\n<!-- COMPONENT:RICH_TEXT:${componentId} -->\n${body}\n<!-- /COMPONENT -->\n\n`;
      }
    };

    // 处理content中可能已存在的组件占位符（清理旧的占位符）
    let cleanContent = content;
    cleanContent = cleanContent.replace(/<!-- COMPONENT:\w+:\w+ -->[\s\S]*?<!-- \/COMPONENT -->/g, '');

    // 为每个block生成唯一ID（如果还没有的话）
    const blocksWithIds = blocks.map(block => {
      if (!block.id) {
        return {
          ...block,
          id: `comp_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`
        };
      }
      return block;
    });

    // 将blocks插入到content中的适当位置
    let resultContent = cleanContent;
    const insertedPositions = [];

    // 首先处理blocks，按顺序插入到content中
    blocksWithIds.forEach(block => {
      const componentId = block.id;
      const componentType = block.__component;

      const placeholderGenerator = componentPlaceholders[componentType];
      if (placeholderGenerator) {
        const placeholder = placeholderGenerator(block, componentId);

        // 简单策略：将所有blocks按顺序添加到content末尾
        resultContent += placeholder;
        insertedPositions.push({
          id: componentId,
          type: componentType,
          placeholder
        });
      }
    });

    return resultContent;
  };

  // Load initial data for editing mode
  useEffect(() => {
    if (isEditing && initialData) {
      
      // 编辑模式：使用传入的初始数据
      setTitle(initialData.title || '');
      setDescription(initialData.description || '');

      // 将blocks反向解析到content中
      const fullContent = parseBlocksToContent(initialData.content, initialData.blocks);
      setContent(fullContent || '');

      setSlugField(initialData.slug || '');
      setSelectedCategory(initialData.category || null);

      const coverImageData = initialData.cover || null;

      setCoverImage(coverImageData);
      setBlocks(initialData.blocks || []);
    } else {
      // 创建模式或非编辑状态：清空表单并从localStorage加载草稿
      if (!isEditing) {
                setTitle('');
        setDescription('');
        setContent('');
        setSlugField('');
        setSelectedCategory(null);
        setCoverImage(null);
        setBlocks([]);
      }

      // 从localStorage加载草稿（仅创建模式）
      const savedDraft = localStorage.getItem('articleDraft');
      if (savedDraft && !isEditing) {
        try {
          const draft = JSON.parse(savedDraft);
          setTitle(draft.title || '');
          setDescription(draft.description || '');
          setContent(draft.content || '');
          setSlugField(draft.slug || '');
          setSelectedCategory(draft.category || null);
          setBlocks(draft.blocks || []);
        } catch (error) {
          console.error('Failed to load draft:', error);
        }
      }
    }
  }, [isEditing, initialData]);

  // 优化防抖保存，使用useCallback减少重新计算
  const saveDraft = useCallback(() => {
    if (isEditing) return;

    if (draftSaveTimerRef.current) {
      clearTimeout(draftSaveTimerRef.current);
    }

    draftSaveTimerRef.current = setTimeout(() => {
      // 优化：减少正则表达式处理，只在有组件标记时才解析
      if (!content.includes('<!-- COMPONENT:')) {
        // 如果内容中没有组件标记，只保存草稿
        const draft = {
          title,
          description,
          content,
          slug: slug_field,
          category: selectedCategory,
          blocks: []
        };
        localStorage.setItem('articleDraft', JSON.stringify(draft));
        return;
      }

      // 清理未在内容中引用的组件
      const referencedComponentIds = new Set();
      const componentRegex = /<!-- COMPONENT:(\w+):(\w+) -->/g;
      let match;

      while ((match = componentRegex.exec(content)) !== null) {
        referencedComponentIds.add(match[2]);
      }

      // 过滤掉未引用的组件
      const filteredBlocks = blocks.filter(block => referencedComponentIds.has(block.id));

      // 只在blocks数组发生变化时才更新状态
      if (filteredBlocks.length !== blocks.length) {
        setBlocks(filteredBlocks);
      }

      const draft = {
        title,
        description,
        content,
        slug: slug_field,
        category: selectedCategory,
        blocks: filteredBlocks
      };
      localStorage.setItem('articleDraft', JSON.stringify(draft));
    }, 500); // 减少防抖时间到500ms，提升响应性
  }, [isEditing, content, title, description, slug_field, selectedCategory, blocks]);

  // 自动保存草稿
  useEffect(() => {
    if (title || description || content) {
      saveDraft();
    }
  }, [saveDraft, title, description, content]);

  // 清理定时器
  useEffect(() => {
    return () => {
      if (draftSaveTimerRef.current) {
        clearTimeout(draftSaveTimerRef.current);
      }
    };
  }, []);

  // Markdown toolbar functions
  const insertMarkdown = (before, after = '') => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = content.substring(start, end);
    const newText = before + selectedText + after;
    
    const newContent = content.substring(0, start) + newText + content.substring(end);
    setContent(newContent);
    
    // Re-focus and set cursor position
    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + before.length + selectedText.length + after.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  // Insert component placeholder
  const insertComponent = (componentType) => {
    const componentId = `comp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const componentData = {
      ...getDefaultComponentData(componentType),
      id: componentId
    };
    
    // 添加到blocks数组
    setBlocks(prevBlocks => [...prevBlocks, componentData]);
    
    // 在当前光标位置插入markdown格式的占位符
    const textarea = textareaRef.current;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      
      let placeholder = '';
      switch (componentType) {
        case 'shared.media':
          placeholder = `\n\n<!-- COMPONENT:MEDIA:${componentId} -->\n![媒体组件](# "点击编辑媒体内容")\n<!-- /COMPONENT -->\n\n`;
          break;
        case 'shared.quote':
          placeholder = `\n\n<!-- COMPONENT:QUOTE:${componentId} -->\n> 点击编辑引用内容\n<!-- /COMPONENT -->\n\n`;
          break;

        case 'shared.slider':
          placeholder = `\n\n<!-- COMPONENT:SLIDER:${componentId} -->\n[轮播图片组件 - 点击编辑](#)\n<!-- /COMPONENT -->\n\n`;
          break;
        default:
          placeholder = `\n\n<!-- COMPONENT:${componentType}:${componentId} -->\n组件占位符\n<!-- /COMPONENT -->\n\n`;
      }
      
      const newContent = content.substring(0, start) + placeholder + content.substring(end);
      setContent(newContent);
      
      // 设置光标位置到占位符后面
      setTimeout(() => {
        textarea.focus();
        const newPosition = start + placeholder.length;
        textarea.setSelectionRange(newPosition, newPosition);
      }, 0);
    }
  };

  // 获取组件默认数据
  const getDefaultComponentData = (componentType) => {
    switch (componentType) {
      case 'shared.media':
        return {
          __component: 'shared.media',
          file: null,
          title: '',
          description: ''
        };
      case 'shared.quote':
        return {
          __component: 'shared.quote',
          content: '',
          author: '',
          about: ''
        };
      case 'shared.slider':
        return {
          __component: 'shared.slider',
          files: []
        };
      default:
         return {
           __component: componentType
         };
     }
   };

  // Handle image upload
  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('请选择图片文件');
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      setError('图片大小不能超过5MB');
      return;
    }

    try {
      const uploadResult = await articleService.uploadMedia(file, 'Articles');
      
      setCoverImage({
        id: uploadResult.id,
        url: uploadResult.url,
        preview: uploadResult.url.startsWith('http') ? uploadResult.url : `${API_URL}${uploadResult.url}`
      });
      
      setError('');
    } catch (error) {
      console.error('上传图片失败:', error);
      setError('上传图片失败');
    }
  };

  // Handle image upload (old implementation - keeping for reference)
  const handleImageUploadOld = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('请选择图片文件');
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      setError('图片大小不能超过5MB');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('files', file);

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error('上传失败');
      }

      const uploadedFiles = await response.json();
      const uploadedFile = uploadedFiles[0];
      
      setCoverImage({
        id: uploadedFile.id,
        url: uploadedFile.url,
        preview: uploadedFile.url.startsWith('http') ? uploadedFile.url : `${API_URL}${uploadedFile.url}`
      });
      
      setError('');
    } catch (error) {
      console.error('上传图片失败:', error);
      setError('上传图片失败');
    }
  };

  // 处理媒体编辑
  const handleMediaEdit = (blockId) => {
    setEditingBlockId(blockId);
    setIsMediaModalOpen(true);
  };

  // 处理媒体替换
  const handleMediaReplace = (mediaData) => {
    // 修复file字段格式：如果mediaData.file是对象，提取其id；如果是数字，直接使用
    const fileValue = mediaData.file ?
      (typeof mediaData.file === 'object' ? mediaData.file.id : mediaData.file) :
      null;

    if (editingBlockId) {
      // 替换现有组件的数据
      setBlocks(prevBlocks =>
        prevBlocks.map(block =>
          block.id === editingBlockId
            ? {
                ...block,
                __component: 'shared.media',
                file: fileValue,
                title: mediaData.title || '',
                description: mediaData.description || ''
              }
            : block
        )
      );
      setEditingBlockId(null);
    } else {
      // 新增组件（原有逻辑）
      const componentId = `comp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const componentDataWithId = {
        __component: 'shared.media',
        file: fileValue,
        title: mediaData.title || '',
        description: mediaData.description || '',
        id: componentId
      };
      
      setBlocks(prevBlocks => [...prevBlocks, componentDataWithId]);
      
      // 在当前光标位置插入markdown格式的占位符
      const textarea = textareaRef.current;
      if (textarea) {
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        
        const placeholder = `\n\n<!-- COMPONENT:MEDIA:${componentId} -->\n![${mediaData.title || '媒体组件'}](# "${mediaData.description || '点击编辑媒体内容'}")\n<!-- /COMPONENT -->\n\n`;
        
        const newContent = content.substring(0, start) + placeholder + content.substring(end);
        setContent(newContent);
        
        setTimeout(() => {
          textarea.focus();
          const newPosition = start + placeholder.length;
          textarea.setSelectionRange(newPosition, newPosition);
        }, 0);
      }
    }
  };

  // 处理媒体插入（重命名为handleMediaReplace以统一处理）
  const handleMediaInsert = handleMediaReplace;

  // 处理引用插入
  const handleQuoteInsert = (quoteData) => {
    if (editingBlockId) {
      // 替换现有组件的数据
      setBlocks(prevBlocks => 
        prevBlocks.map(block => 
          block.id === editingBlockId 
            ? { 
                ...block, 
                __component: 'shared.quote',
                content: quoteData.content || '',
                author: quoteData.author || '',
                about: quoteData.about || ''
              }
            : block
        )
      );
      setEditingBlockId(null);
    } else {
      // 新增组件
      const componentId = `comp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const componentDataWithId = {
        __component: 'shared.quote',
        content: quoteData.content || '',
        author: quoteData.author || '',
        about: quoteData.about || '',
        id: componentId
      };
      
      setBlocks(prevBlocks => [...prevBlocks, componentDataWithId]);
      
      // 在当前光标位置插入markdown格式的占位符
      const textarea = textareaRef.current;
      if (textarea) {
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        
        const authorInfo = quoteData.author || quoteData.about ? 
          `<cite>— <span class="author">${quoteData.author || ''}</span>${quoteData.author && quoteData.about ? ', ' : ''}${quoteData.about || ''}</cite>` : '';
        const placeholder = `\n\n<!-- COMPONENT:QUOTE:${componentId} -->\n<blockquote class="shared-quote">\n<p>${quoteData.content || '引用内容'}</p>\n${authorInfo}\n</blockquote>\n<!-- /COMPONENT -->\n\n`;
        
        const newContent = content.substring(0, start) + placeholder + content.substring(end);
        setContent(newContent);
        
        setTimeout(() => {
          textarea.focus();
          const newPosition = start + placeholder.length;
          textarea.setSelectionRange(newPosition, newPosition);
        }, 0);
      }
    }
  };

  // 处理轮播插入
  const handleSliderInsert = (sliderData) => {
    if (editingBlockId) {
      // 替换现有组件的数据
      setBlocks(prevBlocks => 
        prevBlocks.map(block => 
          block.id === editingBlockId 
            ? { 
                ...block, 
                __component: 'shared.slider',
                files: sliderData.files || []
              }
            : block
        )
      );
      setEditingBlockId(null);
    } else {
      // 新增组件
      const componentId = `comp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const componentDataWithId = {
        __component: 'shared.slider',
        files: sliderData.files || [],
        id: componentId
      };
      
      setBlocks(prevBlocks => [...prevBlocks, componentDataWithId]);
      
      // 在当前光标位置插入markdown格式的占位符
      const textarea = textareaRef.current;
      if (textarea) {
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        
        const placeholder = `\n\n<!-- COMPONENT:SLIDER:${componentId} -->\n[轮播图片组件 - ${sliderData.files?.length || 0}张图片](#)\n<!-- /COMPONENT -->\n\n`;
        
        const newContent = content.substring(0, start) + placeholder + content.substring(end);
        setContent(newContent);
        
        setTimeout(() => {
          textarea.focus();
          const newPosition = start + placeholder.length;
          textarea.setSelectionRange(newPosition, newPosition);
        }, 0);
      }
    }
  };

  // 渲染组件预览
  const renderBlockPreview = (block) => {
        const isEditing = editingBlockId === block.id;
    
    switch (block.__component) {
      case 'shared.media':
        return (
          <div 
            className="media-block-preview"
            onClick={() => handleMediaEdit(block.id)}
            style={{
              cursor: 'pointer',
              border: '1px solid transparent',
              borderRadius: '8px',
              padding: '8px',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.border = '1px solid #d1d5db';
              e.target.style.background = '#f9fafb';
            }}
            onMouseLeave={(e) => {
              e.target.style.border = '1px solid transparent';
              e.target.style.background = 'transparent';
            }}
          >
            {block.file ? (
              <div>
                {block.file.mime?.startsWith('image/') ? (
                  <img 
                    src={block.file.url.startsWith('http') ? block.file.url : `${API_URL}${block.file.url}`}
                    alt={block.title || 'Media'}
                    style={{ maxWidth: '100%', height: 'auto', borderRadius: '6px' }}
                  />
                ) : (
                  <div style={{ padding: '12px', background: '#f3f4f6', borderRadius: '6px' }}>
                    📎 {block.file.name}
                  </div>
                )}
                {block.title && <h5 style={{ margin: '8px 0 4px 0' }}>{block.title}</h5>}
                {block.description && <p style={{ margin: '0', color: '#6b7280', fontSize: '14px' }}>{block.description}</p>}
                <div style={{ 
                  fontSize: '12px', 
                  color: '#9ca3af', 
                  marginTop: '4px',
                  textAlign: 'center'
                }}>
                  点击编辑
                </div>
              </div>
            ) : (
              <div style={{ 
                padding: '20px', 
                background: '#f3f4f6', 
                borderRadius: '8px', 
                textAlign: 'center',
                color: '#6b7280',
                border: '2px dashed #d1d5db'
              }}>
                📷 媒体组件 - 点击添加文件
              </div>
            )}
          </div>
        );
      case 'shared.quote':
        return (
          <blockquote 
            style={{ 
              margin: '16px 0', 
              padding: '16px', 
              background: '#f8fafc', 
              borderLeft: '4px solid #3b82f6',
              borderRadius: '0 6px 6px 0',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onClick={() => {
              setEditingBlockId(block.id);
              setIsQuoteModalOpen(true);
            }}
            onMouseEnter={(e) => {
              e.target.style.background = '#f1f5f9';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = '#f8fafc';
            }}
          >
            <p style={{ margin: '0 0 8px 0', fontStyle: 'italic' }}>"{block.content || '点击编辑引用内容'}"</p>
            {(block.author?.trim() || block.about?.trim()) && (
              <footer style={{ fontSize: '12px', color: '#6b7280' }}>
                {block.author?.trim() && <cite>{block.author}</cite>}
                {block.about?.trim() && <span>{block.author?.trim() ? ', ' : ''}{block.about}</span>}
              </footer>
            )}
            <div style={{ 
              fontSize: '12px', 
              color: '#9ca3af', 
              marginTop: '8px',
              textAlign: 'center'
            }}>
              点击编辑
            </div>
          </blockquote>
        );

      case 'shared.slider':
        return (
          <div 
            className="slider-block-preview"
            style={{
              cursor: 'pointer',
              border: '1px solid transparent',
              borderRadius: '8px',
              padding: '12px',
              transition: 'all 0.2s ease'
            }}
            onClick={() => {
              setEditingBlockId(block.id);
              setIsSliderModalOpen(true);
            }}
            onMouseEnter={(e) => {
              e.target.style.border = '1px solid #d1d5db';
              e.target.style.background = '#f9fafb';
            }}
            onMouseLeave={(e) => {
              e.target.style.border = '1px solid transparent';
              e.target.style.background = 'transparent';
            }}
          >
            {block.files && block.files.length > 0 ? (
              <div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
                  {block.files.slice(0, 4).map(file => (
                    <img 
                      key={file.id}
                      src={file.url.startsWith('http') ? file.url : `${API_URL}${file.url}`}
                      alt={file.name}
                      style={{ width: '120px', height: '80px', objectFit: 'cover', borderRadius: '4px' }}
                    />
                  ))}
                  {block.files.length > 4 && (
                    <div style={{
                      width: '120px',
                      height: '80px',
                      background: '#f3f4f6',
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '14px',
                      color: '#6b7280'
                    }}>
                      +{block.files.length - 4}
                    </div>
                  )}
                </div>
                <div style={{ 
                  fontSize: '12px', 
                  color: '#9ca3af', 
                  textAlign: 'center'
                }}>
                  点击编辑轮播图片
                </div>
              </div>
            ) : (
              <div style={{
                padding: '40px',
                textAlign: 'center',
                color: '#9ca3af',
                border: '2px dashed #d1d5db',
                borderRadius: '8px'
              }}>
                <div style={{ fontSize: '14px', marginBottom: '4px' }}>点击添加轮播图片</div>
                <div style={{ fontSize: '12px' }}>支持多张图片上传</div>
              </div>
            )}
          </div>
        );
      default:
        return <div>未知组件类型: {block.__component}</div>;
    }
  };

  // 解析混合内容为有序的blocks数组
  const parseContentToOrderedBlocks = (content, blocks) => {
    const orderedBlocks = [];

    // 如果没有组件占位符，将整个内容作为一个rich-text block
    if (!content.includes('<!-- COMPONENT:')) {
      if (content.trim()) {
        orderedBlocks.push({
          __component: 'shared.rich-text',
          body: content.trim()
        });
      }
      // 添加独立的blocks
      blocks.forEach(block => {
        orderedBlocks.push({
          ...block,
          __component: block.__component
        });
      });
      return orderedBlocks;
    }

    // 匹配组件占位符的正则表达式
    const componentRegex = /<!-- COMPONENT:(\w+):(\w+) -->[\s\S]*?<!-- \/COMPONENT -->/g;
    let lastIndex = 0;
    let match;

    // 创建blocks映射，以componentId为key
    const blocksMap = {};
    blocks.forEach(block => {
      blocksMap[block.id] = block;
    });

    while ((match = componentRegex.exec(content)) !== null) {
      // 添加组件前的文本内容
      const textBefore = content.substring(lastIndex, match.index);
      if (textBefore.trim()) {
        orderedBlocks.push({
          __component: 'shared.rich-text',
          body: textBefore.trim()
        });
      }

      // 解析组件信息
      const componentId = match[2];

      // 查找对应的block数据
      const blockData = blocksMap[componentId];
      if (blockData) {
        orderedBlocks.push({
          ...blockData,
          __component: blockData.__component
        });
      }

      lastIndex = componentRegex.lastIndex;
    }

    // 添加最后一个组件后的文本内容
    const remainingText = content.substring(lastIndex);
    if (remainingText.trim()) {
      orderedBlocks.push({
        __component: 'shared.rich-text',
        body: remainingText.trim()
      });
    }

    return orderedBlocks;
  };


  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!title.trim()) {
      setError('请输入文章标题');
      return;
    }
    
    if (!content.trim() && blocks.length === 0) {
      setError('请输入文章内容或添加内容组件');
      return;
    }

    // Validate category if selected
    if (selectedCategory) {
      const isCategoryValid = await validateCategory(selectedCategory);
      if (!isCategoryValid) {
        setError('选择的分类无效，请重新选择');
        return;
      }
    }

    setIsSubmitting(true);
    setError('');

    try {
      // 检查用户是否有author权限
      if (!user) {
        setError('请先登录后再创建文章');
        setIsSubmitting(false);
        return;
      }

      // 获取author ID，如果用户没有author权限会返回null
      let authorId = null;
      try {
        authorId = await articleService.getOrCreateAuthor(user);
              } catch (error) {
        console.error('❌ 获取author失败:', error);
        // 如果错误信息包含权限相关内容，显示权限提示
        if (error.message.includes('作者权限')) {
          setError(error.message);
          setIsSubmitting(false);
          return;
        }
      }

      // 如果用户没有author权限，提示用户
      if (authorId === null) {
        setError('您的账户尚未开通作者权限，请联系管理员开通创作权限');
        setIsSubmitting(false);
        return;
      }

      // 分离content和blocks内容
      let firstTextContent = '';
      const blocksForSubmission = [];

      // 创建blocks映射，以componentId为key
      const blocksMap = {};
      blocks.forEach(block => {
        blocksMap[block.id] = block;
      });

      // 如果内容包含组件占位符，按顺序处理
      if (content.includes('<!-- COMPONENT:')) {
        const componentRegex = /<!-- COMPONENT:(\w+):(\w+) -->[\s\S]*?<!-- \/COMPONENT -->/g;
        let lastIndex = 0;
        let firstTextExtracted = false;
        let match;

        while ((match = componentRegex.exec(content)) !== null) {
          // 添加组件前的文本内容
          const textBefore = content.substring(lastIndex, match.index);
          if (textBefore.trim()) {
            if (!firstTextExtracted) {
              firstTextContent = textBefore.trim();
              firstTextExtracted = true;
            } else {
              // 后续的文本内容作为rich-text block
              blocksForSubmission.push({
                __component: 'shared.rich-text',
                body: textBefore.trim()
              });
            }
          }

          // 添加组件block
          const componentId = match[2];
          const blockData = blocksMap[componentId];
          if (blockData) {
            blocksForSubmission.push({
              ...blockData,
              __component: blockData.__component
            });
          }

          lastIndex = componentRegex.lastIndex;
        }

        // 添加最后一个组件后的文本内容
        const remainingText = content.substring(lastIndex);
        if (remainingText.trim()) {
          if (!firstTextExtracted) {
            firstTextContent = remainingText.trim();
            firstTextExtracted = true;
          } else {
            blocksForSubmission.push({
              __component: 'shared.rich-text',
              body: remainingText.trim()
            });
          }
        }
      } else {
        // 纯文本内容
        firstTextContent = content.trim();
        // 添加独立的blocks
        blocks.forEach(block => {
          blocksForSubmission.push({
            ...block,
            __component: block.__component
          });
        });
      }


      
      const articleData = {
        title: title.trim(),
        description: description.trim(),
        slug: slug_field.trim(),
        category: selectedCategory ? parseInt(selectedCategory) : null,
        cover: coverImage?.id || null,
        author: authorId,
        publishedAt: new Date().toISOString()
      };

      // 根据Good Writing文章的正确格式：同时包含content和blocks字段
      // 第一个文本内容始终放在content字段中
      if (firstTextContent.trim()) {
        articleData.content = firstTextContent;
      }

      // 所有其他内容（媒体组件和后续文本）都放在blocks数组中
      if (blocksForSubmission.length > 0) {
        // 移除blocks中的id字段，因为Strapi创建时不需要临时id
        const cleanBlocks = blocksForSubmission.map(({ id, ...block }) => block);
        articleData.blocks = cleanBlocks;
      }

      let result;
      if (isEditing && documentId) {
        // 编辑模式：更新现有文章
        result = await articleService.updateArticle(documentId, articleData);
              } else {
        // 创建模式：创建新文章
        result = await articleService.createArticle(articleData);
              }

      // Clear draft and reset all states
      if (!isEditing) {
        localStorage.removeItem('articleDraft');
      }

      // Reset all form states for both create and edit modes
      setTitle('');
      setDescription('');
      setContent('');
      setSlugField('');
      setSelectedCategory(null);
      setCoverImage(null);
      setBlocks([]);
      setError('');
      setIsPreview(false);

      // Show success message
      
      // Call custom onSubmit if provided
      if (onSubmit) {
        onSubmit();
      }

      // Close modal
      onClose();

      // Navigate to article list (only for create mode)
      if (!isEditing) {
        navigate('/articles');
      }
      
    } catch (error) {
      console.error(isEditing ? '更新文章失败:' : '创建文章失败:', error);

      // 分析错误类型并显示相应的错误模态框
      let errorTitle = isEditing ? '更新文章失败' : '创建文章失败';
      let errorMessage = '';
      let errorType = 'error';

      if (error.response?.data?.error) {
        const errorDetail = error.response.data.error;

        // 检查是否是重复字段错误
        if (errorDetail.details?.errors) {
          const duplicateFields = errorDetail.details.errors
            .filter(err => err.path.includes('slug') || err.path.includes('title'))
            .map(err => {
              if (err.path.includes('slug')) return 'URL别名';
              if (err.path.includes('title')) return '标题';
              return err.path;
            });

          if (duplicateFields.length > 0) {
            errorTitle = '内容重复错误';
            errorMessage = `您的${duplicateFields.join('、')}可能与现有内容重复。请修改后重新提交。`;
            errorType = 'duplicate';
          }
        }

        // 检查是否是权限错误
        if (error.response.status === 403) {
          errorTitle = '权限不足';
          errorMessage = '您没有权限执行此操作。请确认您已登录且具备相应的创作权限。';
          errorType = 'permission';
        }

        // 检查是否是网络错误
        if (error.code === 'NETWORK_ERROR' || error.message.includes('Network Error')) {
          errorTitle = '网络连接错误';
          errorMessage = '无法连接到服务器，请检查您的网络连接后重试。';
          errorType = 'network';
        }

        // 如果没有特定错误，使用通用错误信息
        if (!errorMessage) {
          errorMessage = errorDetail.message || '服务器处理请求时发生错误，请稍后重试。';
        }
      } else if (error.message) {
        errorMessage = error.message;
      } else {
        errorMessage = isEditing ? '更新文章时发生未知错误，请重试' : '创建文章时发生未知错误，请重试';
      }

      showErrorModal(errorTitle, errorMessage, errorType);
    } finally {
      setIsSubmitting(false);
    }
  };

  // 显示错误模态框的函数
  const showErrorModal = (title, message, type = 'error') => {
    setErrorModal({
      isOpen: true,
      title,
      message,
      type
    });
  };

  // 关闭错误模态框
  const closeErrorModal = () => {
    setErrorModal({
      isOpen: false,
      title: '',
      message: '',
      type: 'error'
    });
  };

  // 禁用点击空白区域关闭弹窗的功能
  // 原handleOverlayClick函数已移除

  if (!isOpen) return null;
  
  return (
    <div className="modal-overlay">
      <div className="create-article-modal" onClick={e => e.stopPropagation()}>
        <div className="article-modal-header">
          <h2>{isEditing ? '编辑文章' : '创建文章'}</h2>
          <div className="modal-header-actions">
            <button 
              type="button" 
              className="btn-secondary"
              onClick={() => {
                // 关闭模态窗口
                onClose();
              }}
            >
              取消
            </button>
            <button 
              type="button" 
              className="btn-toggle"
              onClick={() => setIsPreview(!isPreview)}
            >
              {isPreview ? '编辑' : '预览'}
            </button>
            <button 
              type="button" 
              className="btn-primary"
              onClick={handleSubmit}
              disabled={!title.trim() || (!content.trim() && blocks.length === 0) || isSubmitting}
            >
              {isSubmitting ? <SpinnerIcon /> : null}
              {isEditing ? '更新文章' : '发布文章'}
            </button>
          </div>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <form className="article-modal-form" onSubmit={handleSubmit}>
          <div className="article-modal-layout">
            {/* Left sidebar: Basic info */}
            <div className="article-modal-sidebar">
              <div className="modal-sidebar-section">
                <h3>基本信息</h3>
                
                <div className="form-group">
                  <label htmlFor="modal-title">标题 *</label>
                  <input
                    id="modal-title"
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="输入文章标题"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="modal-slug">URL别名</label>
                  <input
                    id="modal-slug"
                    type="text"
                    value={slug_field}
                    onChange={(e) => setSlugField(e.target.value)}
                    placeholder="手动输入URL别名（英文、数字、连字符）"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="modal-description">描述</label>
                  <textarea
                    id="modal-description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="简短描述文章内容"
                    rows="3"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="modal-category">分类</label>
                  <select
                    id="modal-category"
                    value={selectedCategory || ''}
                    onChange={(e) => setSelectedCategory(e.target.value || null)}
                  >
                    <option value="">选择分类</option>
                    {categories.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.attributes?.name || category.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Cover image */}
              <div className="modal-sidebar-section">
                <h3>封面图片</h3>
                <div className="modal-cover-upload">
                  {coverImage ? (
                    <div className="modal-cover-preview">
                                            <img src={coverImage.preview} alt="封面预览" />
                      <button 
                        type="button" 
                        className="remove-cover"
                        onClick={() => setCoverImage(null)}
                      >
                        <CloseIcon />
                      </button>
                    </div>
                  ) : (
                    <div className="modal-cover-upload-area" onClick={() => fileInputRef.current?.click()}>
                      <ImageIcon size={24} />
                      <span>点击上传封面图片</span>
                      <small>支持 JPG、PNG 格式，最大 5MB</small>
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    style={{ display: 'none' }}
                  />
                </div>
              </div>
            </div>

            {/* Right main: Content editor */}
            <div className="article-modal-main">
              <div className="modal-content-section">
                {!isPreview ? (
                  <div className="modal-markdown-editor">
                    {/* Toolbar */}
                    <div className="modal-editor-toolbar">
                      <button type="button" onClick={() => insertMarkdown('**', '**')} title="粗体">
                        <BoldIcon />
                      </button>
                      <button type="button" onClick={() => insertMarkdown('*', '*')} title="斜体">
                        <ItalicIcon />
                      </button>
                      <button type="button" onClick={() => insertMarkdown('[', '](url)')} title="链接">
                        <LinkIcon />
                      </button>
                      <button type="button" onClick={() => insertMarkdown('\n- ', '')} title="列表">
                        <ListIcon />
                      </button>
                      <button type="button" onClick={() => insertMarkdown('\n> ', '')} title="引用">
                        <QuoteIcon />
                      </button>
                      <button type="button" onClick={() => insertMarkdown('`', '`')} title="代码">
                        <CodeIcon />
                      </button>
                      {/* 分隔符 */}
                      <div className="toolbar-separator"></div>
                      
                      {/* 动态组件按钮 */}
                      <button type="button" onClick={() => setIsMediaModalOpen(true)} title="插入媒体（图片、视频、音频）">
                        <ImageIcon />
                        <span className="toolbar-label">媒体</span>
                      </button>
                      <button type="button" onClick={() => setIsQuoteModalOpen(true)} title="插入引用组件">
                        <QuoteIcon />
                        <span className="toolbar-label">引用</span>
                      </button>

                      <button type="button" onClick={() => setIsSliderModalOpen(true)} title="插入轮播组件">
                        <SlidersIcon />
                        <span className="toolbar-label">轮播</span>
                      </button>
                    </div>
                    
                    {/* Text editor */}
                    <textarea
                      ref={textareaRef}
                      value={content}
                      onChange={(e) => {
                        // 安全处理用户输入
                        const newContent = e.target.value;
                        if (newContent !== content) {
                          setContent(newContent);
                        }
                      }}
                      placeholder="开始写作..."
                      className="modal-content-textarea"
                    />
                  </div>
                ) : (
                  <div className="modal-markdown-preview">
                    <MarkdownRenderer 
                      content={content || '暂无内容'} 
                      type="article"
                      enableLineBreaks={true}
                      customComponents={{
                        // 自定义图片组件处理
                        img: ({node, src, alt, title, ...props}) => {
                          // 检查是否是组件占位符
                          if (src === '#' && title) {
                            // 从当前内容中查找对应的组件ID
                            const regex = new RegExp(`<!-- COMPONENT:MEDIA:(\\w+) -->\\s*!\\[${alt.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\]\\(# "${title.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"\\)\\s*<!-- \\/COMPONENT -->`);
                            const match = content.match(regex);
                                                        if (match) {
                              const componentId = match[1];
                              const block = blocks.find(b => b.id === componentId);
                                                            if (block) {
                                return renderBlockPreview(block);
                              }
                            }
                            // 如果找不到对应的组件或组件已被删除，显示占位符
                            return (
                              <div style={{ 
                                padding: '20px', 
                                background: '#f3f4f6', 
                                borderRadius: '8px', 
                                textAlign: 'center',
                                color: '#6b7280',
                                border: '2px dashed #d1d5db'
                              }}>
                                📷 {alt} - {title}
                              </div>
                            );
                          }
                          
                          // 处理普通图片
                          const imgSrc = src && src.startsWith('/') ? `${API_URL}${src}` : src;
                          return (
                            <img 
                              {...props}
                              src={imgSrc} 
                              alt={alt || ''}
                              title={title || alt || ''}
                              className="responsive-image" 
                              loading="lazy"
                            />
                          );
                        }
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>


        </form>
      </div>
      
      {/* 媒体上传弹窗 */}
      <MediaUploadModal
        isOpen={isMediaModalOpen}
        onClose={() => {
          setIsMediaModalOpen(false);
          setEditingBlockId(null);
        }}
        onInsert={handleMediaInsert}
        editingData={editingBlockId ? blocks.find(b => b.id === editingBlockId) : null}
      />
      
      {/* 引用弹窗 */}
      <QuoteModal
        isOpen={isQuoteModalOpen}
        onClose={() => {
          setIsQuoteModalOpen(false);
          setEditingBlockId(null);
        }}
        onInsert={handleQuoteInsert}
        editingData={editingBlockId ? blocks.find(b => b.id === editingBlockId) : null}
      />
      
      {/* 轮播弹窗 */}
      <SliderModal
        isOpen={isSliderModalOpen}
        onClose={() => {
          setIsSliderModalOpen(false);
          setEditingBlockId(null);
        }}
        onInsert={handleSliderInsert}
        editingData={editingBlockId ? blocks.find(b => b.id === editingBlockId) : null}
      />

      {/* 错误提示弹窗 */}
      <ErrorModal
        isOpen={errorModal.isOpen}
        onClose={closeErrorModal}
        title={errorModal.title}
        message={errorModal.message}
        type={errorModal.type}
      />
    </div>
  );
};

export default CreateArticleModal;